import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { monthsApi, membersApi, savingsPlansApi, debtPlansApi } from './services/api';
import { monthLabel } from './utils/format';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UnsavedChangesProvider, useUnsavedChanges } from './context/UnsavedChanges';
import AuthPage        from './pages/AuthPage';
import LandingPage     from './pages/LandingPage';
import BudgetTab       from './pages/BudgetTab';
import TransactionsTab from './pages/TransactionsTab';
import AccountsTab     from './pages/AccountsTab';
import StrategyTab     from './pages/StrategyTab';
import SetupTab        from './pages/SetupTab';
// Trends pulls in the charting library, which is bigger than the rest of the
// app put together. Loading it only when the tab is opened keeps first paint
// the same for everyone who never goes there.
const TrendsTab = lazy(() => import('./pages/TrendsTab'));
import SecurityModal    from './components/SecurityModal';
import FeedbackModal    from './components/FeedbackModal';
import HelpModal        from './components/HelpModal';
import UpdateNotice     from './components/UpdateNotice';
import { UPDATE_ID }    from './updates';
import OnboardingWizard from './components/OnboardingWizard';
import Labrador        from './components/Labrador';
import './App.css';

const TABS = ['Budget', 'Ledger', 'Accounts', 'Strategy', 'Trends', 'Setup'];

export default function App() {
  return (
    <AuthProvider>
      <UnsavedChangesProvider>
        <AuthGate />
      </UnsavedChangesProvider>
    </AuthProvider>
  );
}

function AuthGate() {
  const { user, checking } = useAuth();
  // Signed-out visitors see the landing page first; an emailed verification
  // link (?verify=TOKEN) must land directly on AuthPage, which handles it.
  const [view, setView] = useState(() =>
    window.location.search.includes('verify=') ? 'auth' : 'landing');
  const [authMode, setAuthMode] = useState('login');

  if (checking) return <div className="spinner" style={{ marginTop: 120 }} />;
  if (!user) {
    if (view === 'landing') {
      return <LandingPage onEnter={(mode) => { setAuthMode(mode || 'login'); setView('auth'); }} />;
    }
    return <AuthPage initialMode={authMode} onBack={() => setView('landing')} />;
  }
  // Key by user id so all state resets cleanly when switching accounts
  return <BudgetApp key={user.id} />;
}

function BudgetApp() {
  const { user, logout } = useAuth();
  const { confirmDiscard } = useUnsavedChanges();
  // Any navigation that would unmount a tab holding unsaved edits asks first.
  const guardNav = (go) => { if (confirmDiscard()) go(); };
  const [months,     setMonths]     = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [activeTab,  setActiveTab]  = useState('Budget');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [splash,     setSplash]     = useState(true);
  const [splashFade, setSplashFade] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHelp,     setShowHelp]     = useState(false);
  // Shown once per user per update. Dismissal is remembered locally.
  const updateKey = `tb_seen_${UPDATE_ID}_${user.id}`;
  const [updateSeen, setUpdateSeen] = useState(() => localStorage.getItem(updateKey) === '1');
  const dismissUpdate = () => { localStorage.setItem(updateKey, '1'); setUpdateSeen(true); };
  const [householdEmpty, setHouseholdEmpty] = useState(null); // null = not yet known
  const [wizardDone, setWizardDone] = useState(() => localStorage.getItem(`tb_wizard_done_${user.id}`) === '1');
  const [reloadKey, setReloadKey] = useState(0); // bump to remount the active tab after onboarding

  const loadMonths = useCallback(async () => {
    const res = await monthsApi.getAll();
    return res.data;
  }, []);

  useEffect(() => {
    const today = new Date();
    monthsApi.init(today.getFullYear(), today.getMonth())
      .then(() => loadMonths())
      .then(list => {
        setMonths(list);
        const idx = list.findIndex(m =>
          m.year === today.getFullYear() && m.month === today.getMonth());
        setCurrentIdx(idx >= 0 ? idx : list.length - 1);
        setLoading(false);
        // Splash: hold for 1.8s then fade out
        setTimeout(() => {
          setSplashFade(true);
          setTimeout(() => setSplash(false), 700);
        }, 1800);
      })
      .catch(() => {
        setError('Cannot reach the backend. Make sure Spring Boot is running on port 8080.');
        setLoading(false);
        setSplash(false);
      });
  }, [loadMonths]);

  const refresh = useCallback(async () => {
    const list = await loadMonths();
    setMonths(list);
  }, [loadMonths]);

  // Decide whether to show the first-run wizard: a brand-new household has no
  // members, savings plans, or debts.
  useEffect(() => {
    Promise.all([membersApi.getAll(), savingsPlansApi.getAll(), debtPlansApi.getAll()])
      .then(([m, s, d]) => setHouseholdEmpty(m.data.length === 0 && s.data.length === 0 && d.data.length === 0))
      .catch(() => setHouseholdEmpty(false));
  }, []);

  const handleWizardDone = useCallback(() => {
    setWizardDone(true);
    setHouseholdEmpty(false);
    setActiveTab('Budget');
    setReloadKey(k => k + 1);   // remount the active tab so it refetches the new data
    refresh();
  }, [refresh]);

  const handleNextMonth = useCallback(async () => {
    const cur = months[currentIdx];
    if (!cur) return;
    await monthsApi.createNext(cur.id);
    const list = await loadMonths();
    setMonths(list);
    setCurrentIdx(list.length - 1);
  }, [months, currentIdx, loadMonths]);

  const currentMonth = months[currentIdx];

  const tabComponent = () => {
    if (!currentMonth) return null;
    switch (activeTab) {
      case 'Budget':   return <BudgetTab       month={currentMonth} onRefresh={refresh} />;
      case 'Ledger':   return <TransactionsTab  month={currentMonth} onRefresh={refresh} />;
      case 'Accounts': return <AccountsTab      month={currentMonth} onRefresh={refresh} />;
      case 'Strategy': return <StrategyTab      month={currentMonth} onRefresh={refresh} />;
      // Trends spans every month, so it takes no month prop.
      case 'Trends':   return (
        <Suspense fallback={<div className="spinner" />}><TrendsTab /></Suspense>
      );
      case 'Setup':    return <SetupTab         month={currentMonth} onRefresh={refresh} />;
      default:         return null;
    }
  };

  return (
    <>
      {/* Splash screen */}
      {splash && (
        <div className={`splash-screen${splashFade ? ' fade-out' : ''}`}>
          <div className="splash-inner">
            <div className="splash-runner">
              <Labrador size={104} className="splash-mark" animated />
            </div>
            <div className="splash-logo">TRIGGER BUDGET</div>
            <div className="splash-tagline">Run Wild. Spend Smart.</div>
          </div>
        </div>
      )}

      <div className="app">
        {error && <div className="splash error">{error}</div>}

        {!loading && !error && (
          <>
            <header className="app-header">
              <div className="brand">
                <Labrador size={24} className="brand-mark" />
                <span>TRIGGER BUDGET</span>
                <span className="beta-badge">BETA</span>
              </div>
              <div className="month-nav">
                <button className="btn btn-sm" onClick={() => guardNav(() => setCurrentIdx(i => Math.max(0, i - 1)))}
                  disabled={currentIdx === 0}>‹</button>
                <span className="month-label">{monthLabel(currentMonth)}</span>
                <button className="btn btn-sm" onClick={() => guardNav(() => setCurrentIdx(i => Math.min(months.length - 1, i + 1)))}
                  disabled={currentIdx === months.length - 1}>›</button>
                <button className="btn btn-primary btn-sm" onClick={() => guardNav(handleNextMonth)}>+ Month</button>
              </div>
              <div className="header-actions">
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{user.email}</span>
                <button className="btn btn-sm" onClick={() => setShowHelp(true)}>Help</button>
                <button className="btn btn-sm" onClick={() => setShowFeedback(true)}>Feedback</button>
                <button className="btn btn-sm" onClick={() => setShowSecurity(true)}>Security</button>
                <button className="btn btn-sm" onClick={logout}>Sign out</button>
              </div>
            </header>

            <nav className="tab-bar">
              {TABS.map(t => (
                <button key={t} className={`tab-btn${activeTab === t ? ' active' : ''}`}
                  onClick={() => guardNav(() => setActiveTab(t))}>{t}</button>
              ))}
            </nav>

            <main className="app-main" key={reloadKey}>
              {tabComponent()}
            </main>

            {showSecurity && <SecurityModal onClose={() => setShowSecurity(false)} />}
            {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} context={activeTab} />}
            {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

            {currentMonth && householdEmpty && !wizardDone && (
              <OnboardingWizard month={currentMonth} userId={user.id} onDone={handleWizardDone} />
            )}

            {/* Held back until onboarding is done and the splash has cleared, so a
                brand-new user isn't met with two modals stacked on each other. */}
            {!updateSeen && !splash && !(householdEmpty && !wizardDone) && (
              <UpdateNotice onClose={dismissUpdate} />
            )}
          </>
        )}
      </div>
    </>
  );
}
